import i18next from 'i18next';
import moment from 'moment-timezone';

import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { formatDateWithLanguageCode, formatTimeWithLanguageCode } from '@commons/utils/date';
import { getClientStoreNameTranslation } from '@commons/utils/translations';
import utilsXLS from '@commons/utils/makeXLS';

import { EXTERNAL_ORDER_VAT_RATE_DECIMALS, getExternalOrderStatusProperties } from '../constants';

import { buildOrderLineTotals } from './buildOrderLineTotals';
import { getExternalOrderChannelLabel } from './getExternalOrderChannelLabel';

/**
 * Columns of the order detail export, one row per ordered product: the order header repeated on
 * every row, then the line itself, mirroring the products table of the form. The currency column is
 * appended automatically by `generateDefaultSheet`, the columns holding an amount being typed
 * `currency`.
 *
 * @param {Object} params
 * @param {string} params.clientStoreName - The store naming configured on the client
 * @param {boolean} params.hasPreparedQuantities - Whether the order carries BDL quantities
 *   (statuses from `En préparation` onwards)
 *
 * @returns {Array<Object>} The sheet columns settings
 */
const getColumnsSettings = ({ clientStoreName, hasPreparedQuantities }) => {
  const columns = [
    {
      propertyKey: 'reference',
      name: i18next.t('EXTERNAL_ORDERS.FORM.EXPORT_COLUMN_ORDER_REFERENCE'),
    },
    {
      propertyKey: 'storeName',
      name: getClientStoreNameTranslation(clientStoreName),
    },
    {
      propertyKey: 'externalClientName',
      name: i18next.t('EXTERNAL_ORDERS.LIST.COLUMN_CLIENT'),
    },
    {
      propertyKey: 'externalClientReference',
      name: i18next.t('EXTERNAL_ORDERS.FORM.EXPORT_COLUMN_EXTERNAL_CLIENT_REFERENCE'),
    },
    {
      propertyKey: 'deliveryDate',
      name: i18next.t('EXTERNAL_ORDERS.LIST.COLUMN_DELIVERY_DATE'),
    },
    {
      propertyKey: 'createdAt',
      name: i18next.t('GENERAL.CREATION_DATE'),
    },
    {
      propertyKey: 'category',
      name: i18next.t('GENERAL.CATEGORY'),
      transform: (category) => category || i18next.t('GENERAL.NONE_VALUE'),
    },
    {
      propertyKey: 'sku',
      name: i18next.t('GENERAL.SKU'),
    },
    {
      propertyKey: 'name',
      name: i18next.t('EXTERNAL_ORDERS.FORM.EXPORT_COLUMN_PRODUCT_NAME'),
    },
    {
      propertyKey: 'quantity',
      name: i18next.t('EXTERNAL_ORDERS.FORM.COLUMN_QUANTITY_BDC'),
    },
  ];

  if (hasPreparedQuantities) {
    columns.push({
      propertyKey: 'preparedQuantity',
      name: i18next.t('EXTERNAL_ORDERS.FORM.COLUMN_QUANTITY_BDL'),
    });
  }

  columns.push(
    {
      type: 'percentage',
      propertyKey: 'vatRate',
      name: i18next.t('EXTERNAL_ORDERS.FORM.EXPORT_COLUMN_VAT_RATE'),
      decimals: EXTERNAL_ORDER_VAT_RATE_DECIMALS,
    },
    {
      type: 'currency',
      propertyKey: 'totalWithoutTaxes',
      name: i18next.t('EXTERNAL_ORDERS.FORM.COLUMN_TOTAL_WITHOUT_TAXES'),
    },
    {
      type: 'currency',
      propertyKey: 'totalWithTaxes',
      name: i18next.t('EXTERNAL_ORDERS.FORM.COLUMN_TOTAL_WITH_TAXES'),
    },
  );

  if (hasPreparedQuantities) {
    columns.push({
      type: 'currency',
      propertyKey: 'preparedTotalWithTaxes',
      name: i18next.t('EXTERNAL_ORDERS.FORM.COLUMN_TOTAL_BDL'),
    });
  }

  return columns;
};

/** An absent or unparsable date leaves the cell empty rather than writing an invalid value. */
const formatExportDate = (date) => {
  if (!date) {
    return null;
  }

  const parsedDate = moment(date);

  if (!parsedDate.isValid()) {
    return null;
  }

  return parsedDate.format(DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR);
};

/**
 * Rows of the export sheet, one per ordered line. The totals are recomputed from the quantity, the
 * unit price and the VAT rate of each line — the three statuses hand over lines of slightly
 * different shapes, but all of them carry these fields. The BDL fields mirror the screen overlay:
 * an input emptied to `null` weighs 0, exactly as a save would persist it.
 */
const buildExportRows = ({
  reference,
  storeName,
  externalClientName,
  externalClientReference,
  deliveryDate,
  createdAt,
  productLines,
  hasPreparedQuantities,
}) => {
  const formattedDeliveryDate = formatExportDate(deliveryDate);
  const formattedCreatedAt = formatExportDate(createdAt);

  return productLines.map((productLine) => {
    const { category, sku, name, quantity, priceWithTaxes, vatRate, preparedQuantity } =
      productLine;

    const { totalWithTaxes, totalWithoutTaxes } = buildOrderLineTotals(productLine);

    const row = {
      reference,
      storeName,
      externalClientName,
      externalClientReference,
      deliveryDate: formattedDeliveryDate,
      createdAt: formattedCreatedAt,
      category,
      sku,
      name,
      quantity,
      vatRate,
      totalWithoutTaxes,
      totalWithTaxes,
    };

    if (!hasPreparedQuantities) {
      return row;
    }

    return {
      ...row,
      preparedQuantity: preparedQuantity ?? 0,
      preparedTotalWithTaxes: (preparedQuantity ?? 0) * priceWithTaxes,
    };
  });
};

/**
 * Client-side XLSX generation of an external order detail (COR-1687): no dedicated route, the file
 * is built from the payload already displayed by the form. The export always carries the whole
 * order — never a selection nor the searched subset — and is available whatever the rights, the
 * page being accessible. The file and its single sheet are named after the order reference.
 *
 * @param {Object} params
 * @param {string} params.reference - The order reference, also naming the file and the sheet
 * @param {string} params.clientStoreName - The store naming configured on the client
 * @param {string} [params.storeName] - The name of the store preparing the order
 * @param {string} [params.externalClientName] - The name of the external client
 * @param {string} [params.externalClientReference] - The external reference of the client
 * @param {Object|string} [params.deliveryDate] - The delivery date of the order
 * @param {string} [params.createdAt] - The creation date of the order
 * @param {Array<Object>} params.productLines - The ordered lines, quantity / price / VAT included
 * @param {boolean} [params.hasPreparedQuantities] - Whether to export the BDL columns
 * @param {Object} params.currency - The client currency, appended next to the amounts
 *
 * @returns {void}
 */
export const exportExternalOrder = ({
  reference,
  clientStoreName,
  storeName,
  externalClientName,
  externalClientReference,
  deliveryDate,
  createdAt,
  productLines,
  hasPreparedQuantities = false,
  currency,
}) => {
  const sheet = utilsXLS.generateDefaultSheet(
    reference,
    getColumnsSettings({ clientStoreName, hasPreparedQuantities }),
    buildExportRows({
      reference,
      storeName,
      externalClientName,
      externalClientReference,
      deliveryDate,
      createdAt,
      productLines,
      hasPreparedQuantities,
    }),
    currency,
  );

  utilsXLS.makeXLS(reference, [sheet]);
};

/**
 * Columns of the list export, mirroring the ones of the list (getExternalOrderColumns): the `Canal`
 * column only exists on a multi-channel platform, and the `Devise` column is appended by
 * `generateDefaultSheet` since the total carries the `currency` type.
 *
 * @param {Object} params
 * @param {string} params.userLanguageCode - The language code formatting the delivery date and time
 * @param {string} params.storeName - The client wording for a store
 * @param {boolean} params.hasMultipleChannels - Whether the platform handles several channels
 *
 * @returns {Array<Object>} The sheet columns settings
 */
const getListColumnsSettings = ({ userLanguageCode, storeName, hasMultipleChannels }) => {
  const statusProperties = getExternalOrderStatusProperties();

  const columns = [
    {
      propertyKey: 'externalClientName',
      name: i18next.t('EXTERNAL_ORDERS.LIST.COLUMN_CLIENT'),
    },
    {
      propertyKey: 'storeName',
      name: getClientStoreNameTranslation(storeName),
    },
    {
      propertyKey: 'deliveryDate',
      name: i18next.t('EXTERNAL_ORDERS.LIST.COLUMN_DELIVERY_DATE'),
      transform: (deliveryDate) => {
        if (!deliveryDate) {
          return null;
        }

        return formatDateWithLanguageCode(moment(deliveryDate), userLanguageCode);
      },
    },
    {
      propertyKey: 'deliveryTime',
      name: i18next.t('EXTERNAL_ORDERS.LIST.COLUMN_DELIVERY_TIME'),
      transform: (deliveryTime) => {
        if (!deliveryTime) {
          return null;
        }

        return formatTimeWithLanguageCode(deliveryTime, userLanguageCode);
      },
    },
  ];

  if (hasMultipleChannels) {
    columns.push({
      propertyKey: 'channelLabel',
      name: i18next.t('EXTERNAL_ORDERS.LIST.COLUMN_CHANNEL'),
    });
  }

  columns.push(
    {
      type: 'currency',
      propertyKey: 'totalWithTaxes',
      name: i18next.t('EXTERNAL_ORDERS.LIST.COLUMN_TOTAL_WITH_TAXES'),
    },
    {
      propertyKey: 'status',
      name: i18next.t('EXTERNAL_ORDERS.LIST.COLUMN_STATUS'),
      transform: (status) => statusProperties[status]?.name ?? null,
    },
  );

  return columns;
};

/**
 * Client-side XLSX generation of the external orders list. The rows are the flat ones of the list,
 * whose `totalWithTaxes` is already the amount their status calls for — the ordered one (BDC) up to
 * `A venir`, the prepared one (BDL) from `En préparation` onwards.
 *
 * @param {Array<Object>} externalOrders - The orders to write in the file
 * @param {Object} params
 * @param {string} params.userLanguageCode - The language code formatting the delivery date and time
 * @param {string} params.storeName - The client wording for a store
 * @param {boolean} params.hasMultipleChannels - Whether the platform handles several channels
 * @param {Object} params.currency - The client currency, written in the appended `Devise` column
 *
 * @returns {void}
 */
export const exportExternalOrders = (
  externalOrders,
  { userLanguageCode, storeName, hasMultipleChannels, currency },
) => {
  // An export transform only receives the value of its own property, which is not enough to resolve
  // the channel: its label is held by a translation key carried apart on the row.
  const rows = externalOrders.map((externalOrder) => ({
    ...externalOrder,
    channelLabel: getExternalOrderChannelLabel(externalOrder),
  }));

  const sheet = utilsXLS.generateDefaultSheet(
    i18next.t('FEATURE.EXTERNAL.ORDERS'),
    getListColumnsSettings({ userLanguageCode, storeName, hasMultipleChannels }),
    rows,
    currency,
  );

  utilsXLS.makeXLS(i18next.t('EXTERNAL_ORDERS.LIST.EXPORT_FILENAME'), [sheet]);
};
